// src/components/landing/animations/ConvertAnimation.js
import React from 'react';
import { motion } from 'framer-motion';

/**
 * CONVERT ANIMATION
 * 
 * Shows file formats (shapes) falling through a laser beam and converting to PDFs.
 * Uses SVG clipPath to create a "wipe" effect at the laser line.
 * 
 * Animation sequence (per column):
 * 1. Shape falls from top, tumbling
 * 2. As it crosses laser beam, top portion stays as shape, bottom becomes document
 * 3. Document continues falling with same tumble
 * 4. Loop with different shapes
 * 
 * Frame: 1024x2000px
 */

const FRAME = { width: 1024, height: 2000 }; // Taller frame for more vertical space
const LASER_Y = 1000; // Middle of frame
const ANIMATION_DURATION = 3.5; // seconds per drop (slower)
const SHAPE_STAGGER = 4.5; // seconds between shapes in same column (DURATION + buffer)

const ConvertAnimation = ({ scrollProgress }) => {
  // Random rotation values for variety (between 160-200 and -160 to -200)
  const rotationOptions = [170, 185, 195, -170, -185, -195];
  
  // Column positions with varied stagger to avoid simultaneous drops
  const columns = [
    { 
      x: 256, 
      shapes: ['square', 'triangle', 'oval'],
      delayOffset: 0,
      rotations: [rotationOptions[0], rotationOptions[3], rotationOptions[1]] // Mix positive and negative
    },
    { 
      x: 512, 
      shapes: ['triangle', 'oval', 'square'],
      delayOffset: 1.4, // Offset to avoid overlap with column 1
      rotations: [rotationOptions[4], rotationOptions[2], rotationOptions[5]]
    },
    { 
      x: 768, 
      shapes: ['oval', 'square', 'triangle'],
      delayOffset: .6, // Different offset
      rotations: [rotationOptions[1], rotationOptions[3], rotationOptions[4]]
    },
  ];

  return (
    <motion.div 
      className="w-full h-full flex items-center justify-center overflow-visible"
      style={{ 
        opacity: scrollProgress ? 1 : 1 // Could add fade if needed
      }}
    >
      <svg
        viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
        className="w-full h-auto"
      >
        <defs>
          {/* Clip region ABOVE the laser (for shapes) */}
          <clipPath id="above-laser">
            <rect x="0" y="0" width={FRAME.width} height={LASER_Y} />
          </clipPath>
          
          {/* Clip region BELOW the laser (for documents) */}
          <clipPath id="below-laser">
            <rect x="0" y={LASER_Y} width={FRAME.width} height={FRAME.height - LASER_Y} />
          </clipPath>
        </defs>

        {/* ANIMATED COLUMNS */}
        {columns.map((column, columnIndex) => (
          <g key={columnIndex}>
            {column.shapes.map((shapeType, shapeIndex) => {
              const delay = column.delayOffset + shapeIndex * SHAPE_STAGGER;
              const rotation = column.rotations[shapeIndex];
              
              return (
                <g key={`${columnIndex}-${shapeIndex}`}>
                  {/* SHAPE - clipped to above laser, parent g has clipPath */}
                  <g clipPath="url(#above-laser)">
                    <motion.g
                      animate={{
                        y: [-400, FRAME.height + 400],
                        rotate: [0, rotation * 2], // Full rotation (double to get 340-400° range)
                      }}
                      transition={{
                        duration: ANIMATION_DURATION,
                        delay: delay,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <g transform={`translate(${column.x}, 0)`}>
                        {renderShape(shapeType)}
                      </g>
                    </motion.g>
                  </g>

                  {/* DOCUMENT - clipped to below laser, parent g has clipPath */}
                  <g clipPath="url(#below-laser)">
                    <motion.g
                      animate={{
                        y: [-400, FRAME.height + 400],
                        rotate: [0, rotation * 2], // Same rotation as shape
                      }}
                      transition={{
                        duration: ANIMATION_DURATION,
                        delay: delay,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <g transform={`translate(${column.x}, 0)`}>
                        <image
                          href="/doc.svg"
                          width={128}
                          height={160}
                          x={-64}
                          y={-80}
                        />
                      </g>
                    </motion.g>
                  </g>
                </g>
              );
            })}
          </g>
        ))}

        {/* LASER BEAM - drawn last so it's on top of everything */}
        <line
          x1={0}
          y1={LASER_Y}
          x2={FRAME.width}
          y2={LASER_Y}
          stroke="#FC766E"
          strokeWidth={12}
          style={{ 
            filter: 'drop-shadow(0 0 6px rgba(252, 118, 110, 0.8))',
            opacity: 0.9
          }}
        />

        {/* LEFT LASER EMITTER */}
        <image
          href="/laser_emitter.svg"
          width={80}
          height={64}
          x={-24}
          y={LASER_Y - 32}
        />

        {/* RIGHT LASER EMITTER (flipped) */}
        <g transform={`translate(${FRAME.width}, ${LASER_Y}) scale(-1, 1) translate(0, -32)`}>
          <image
            href="/laser_emitter.svg"
            width={80}
            height={64}
            x={-24}
            y={0}
          />
        </g>
      </svg>
    </motion.div>
  );
};

function roundedPolygonPathQuad(points, radius) {
  const n = points.length;
  if (n === 0) return "";

  // Precompute tangent points for each corner
  const starts = new Array(n);
  const ends   = new Array(n);

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const cur  = points[i];
    const next = points[(i + 1) % n];

    const vPrev = { x: prev.x - cur.x, y: prev.y - cur.y };
    const vNext = { x: next.x - cur.x, y: next.y - cur.y };

    const lenPrev = Math.hypot(vPrev.x, vPrev.y);
    const lenNext = Math.hypot(vNext.x, vNext.y);

    // distance along edges to start/end the rounded corner (clamped)
    const dPrev = Math.min(radius, lenPrev / 2);
    const dNext = Math.min(radius, lenNext / 2);

    const vPrevN = { x: vPrev.x / (lenPrev || 1), y: vPrev.y / (lenPrev || 1) };
    const vNextN = { x: vNext.x / (lenNext || 1), y: vNext.y / (lenNext || 1) };

    starts[i] = { x: cur.x + vPrevN.x * dPrev, y: cur.y + vPrevN.y * dPrev };
    ends[i]   = { x: cur.x + vNextN.x * dNext, y: cur.y + vNextN.y * dNext };
  }

  // Build path: start at end[last], then for each corner -> line to start[i], Q control=cur -> end[i]
  let d = `M ${ends[n - 1].x} ${ends[n - 1].y}`;
  for (let i = 0; i < n; i++) {
    d += ` L ${starts[i].x} ${starts[i].y}`;
    d += ` Q ${points[i].x} ${points[i].y} ${ends[i].x} ${ends[i].y}`;
  }
  d += " Z";
  return d;
}

/**
 * Render different shape types
 * All shapes are centered at (0, 0) for proper rotation and clipping
 */
const renderShape = (shapeType) => {
  const scale = 0.5; // Scale down to 50% of original size
  
  switch (shapeType) {
    case 'square':
      const squareSize = 288 * scale;
      return (
        <rect
          x={-squareSize / 2} // Center horizontally
          y={-squareSize / 2} // Center vertically
          width={squareSize}
          height={squareSize}
          rx={64 * scale}
          fill="#525C65" // app-gray-700
        />
      );
    
    case "triangle": {
      const h = 278 * scale;
      const w = 307 * scale;
      const r = 96 * scale; // tweak this to taste

      const points = [
        { x: 0,      y: -h * 2/3 },
        { x: w / 2,  y:  h / 3 },
        { x: -w / 2, y:  h / 3 }
      ];

      return (
        <path
          d={roundedPolygonPathQuad(points, r)}
          fill="#8F00FF"
          stroke="none"
        />
      );
    }
    
    case 'oval':
      // Oval (ellipse) instead of circle so rotation is visible
      return (
        <ellipse
          cx={0} // Centered
          cy={0} // Centered
          rx={149 * scale * 1.1} // Wider (130% of radius)
          ry={149 * scale * 0.9} // Shorter (80% of radius)
          fill="#44BBA4" // app-teal
        />
      );
    
    default:
      return null;
  }
};

export default ConvertAnimation;